import createDebug from 'debug';
import { GripVertical, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { QueueResponse } from '@/api/client';
import { Button } from '@/components/ui/button';
import { useCreateQueueMutation, useDeleteQueueMutation, useUpdateQueueMutation } from '@/hooks/useLinks';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const debug = createDebug('app:queue-mgmt');

// Preset colors for quick selection
const PRESET_COLORS = [
	'#ef4444', // red
	'#f97316', // orange
	'#eab308', // yellow
	'#22c55e', // green
	'#14b8a6', // teal
	'#3b82f6', // blue
	'#6366f1', // indigo
	'#8b5cf6', // violet
	'#ec4899', // pink
	'#6b7280', // gray
];

interface QueueManagementDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	queues: QueueResponse[];
	onQueuesChanged: (queues: QueueResponse[]) => void;
}

type EditMode = 'list' | 'create' | 'edit';

interface QueueFormData {
	name: string;
	slug: string;
	color: string;
	icon: string;
}

function slugify(text: string): string {
	return text
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}

export function QueueManagementDialog({
	open,
	onOpenChange,
	queues,
	onQueuesChanged,
}: QueueManagementDialogProps) {
	const [mode, setMode] = useState<EditMode>('list');
	const [editingQueue, setEditingQueue] = useState<QueueResponse | null>(null);
	const [formData, setFormData] = useState<QueueFormData>({
		name: '',
		slug: '',
		color: '#6366f1',
		icon: '',
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	const createQueueMutation = useCreateQueueMutation();
	const updateQueueMutation = useUpdateQueueMutation();
	const deleteQueueMutation = useDeleteQueueMutation();

	// Filter out the virtual "incoming" queue (id=0) - it's not editable
	const editableQueues = queues.filter((q) => q.id !== 0);

	// Reset state when dialog opens/closes
	useEffect(() => {
		if (!open) {
			setMode('list');
			setEditingQueue(null);
			setFormData({ name: '', slug: '', color: '#6366f1', icon: '' });
			setError(null);
			setDeleteConfirmId(null);
		}
	}, [open]);

	// Auto-generate slug from name when creating
	function handleNameChange(name: string) {
		setFormData((prev) => ({
			...prev,
			name,
			slug: mode === 'create' ? slugify(name) : prev.slug,
		}));
	}

	function handleStartCreate() {
		setFormData({ name: '', slug: '', color: '#6366f1', icon: '' });
		setMode('create');
		setError(null);
	}

	function handleStartEdit(queue: QueueResponse) {
		setEditingQueue(queue);
		setFormData({
			name: queue.name,
			slug: queue.slug,
			color: queue.color,
			icon: queue.icon ?? '',
		});
		setMode('edit');
		setError(null);
	}

	function handleCancel() {
		setMode('list');
		setEditingQueue(null);
		setError(null);
	}

	const handleSubmit = useCallback(async () => {
		if (!formData.name.trim() || !formData.slug.trim()) {
			setError('Name and slug are required');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			if (mode === 'create') {
				debug('creating queue via GraphQL: %o', formData);
				const newQueue = await createQueueMutation.mutateAsync({
					name: formData.name.trim(),
					slug: formData.slug.trim(),
					color: formData.color,
					icon: formData.icon.trim() || undefined,
				});
				if (newQueue) {
					debug('created queue: %o', newQueue);
					onQueuesChanged([...queues, newQueue]);
				}
			} else if (mode === 'edit' && editingQueue) {
				debug('updating queue %d via GraphQL: %o', editingQueue.id, formData);
				const updated = await updateQueueMutation.mutateAsync(editingQueue.id, {
					name: formData.name.trim(),
					slug: formData.slug.trim(),
					color: formData.color,
					icon: formData.icon.trim() || undefined,
				});
				if (updated) {
					debug('updated queue: %o', updated);
					onQueuesChanged(queues.map((q) => (q.id === updated.id ? updated : q)));
				}
			}
			setMode('list');
			setEditingQueue(null);
		} catch (err) {
			debug('error saving queue: %o', err);
			setError(err instanceof Error ? err.message : 'Failed to save queue');
		} finally {
			setIsSubmitting(false);
		}
	}, [mode, formData, editingQueue, queues, onQueuesChanged, createQueueMutation, updateQueueMutation]);

	const handleDelete = useCallback(
		async (queueId: number) => {
			if (deleteConfirmId !== queueId) {
				setDeleteConfirmId(queueId);
				return;
			}

			setIsSubmitting(true);
			try {
				debug('deleting queue %d via GraphQL', queueId);
				await deleteQueueMutation.mutateAsync(queueId);
				onQueuesChanged(queues.filter((q) => q.id !== queueId));
				setDeleteConfirmId(null);
			} catch (err) {
				debug('error deleting queue: %o', err);
				setError(err instanceof Error ? err.message : 'Failed to delete queue');
			} finally {
				setIsSubmitting(false);
			}
		},
		[deleteConfirmId, queues, onQueuesChanged, deleteQueueMutation]
	);

	// Render queue list
	function renderList() {
		return (
			<>
				<DialogHeader>
					<DialogTitle>Manage Queues</DialogTitle>
					<DialogDescription>
						Create, edit, and organize your queues. Links in deleted queues move to Incoming.
					</DialogDescription>
				</DialogHeader>

				<div className="py-4 space-y-2">
					{editableQueues.length === 0 ? (
						<p className="text-center text-muted-foreground py-8">
							No queues yet. Create one to get started!
						</p>
					) : (
						editableQueues.map((queue) => (
							<div
								key={queue.id}
								className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
							>
								<GripVertical className="size-4 text-muted-foreground cursor-grab" />
								<span
									className="size-4 rounded-full shrink-0"
									style={{ backgroundColor: queue.color }}
								/>
								{queue.icon && <span className="text-lg">{queue.icon}</span>}
								<div className="flex-1 min-w-0">
									<p className="font-medium truncate">{queue.name}</p>
									<p className="text-xs text-muted-foreground">/{queue.slug}</p>
								</div>
								<Button variant="ghost" size="sm" onClick={() => handleStartEdit(queue)}>
									<Pencil className="size-4" />
								</Button>
								<Button
									variant="ghost"
									size="sm"
									className={cn(
										deleteConfirmId === queue.id &&
											'bg-destructive text-destructive-foreground hover:bg-destructive/90'
									)}
									onClick={() => handleDelete(queue.id)}
									disabled={isSubmitting}
								>
									{deleteConfirmId === queue.id ? (
										<span className="text-xs">Confirm?</span>
									) : (
										<Trash2 className="size-4" />
									)}
								</Button>
							</div>
						))
					)}
				</div>

				{error && <p className="text-sm text-destructive">{error}</p>}

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Close
					</Button>
					<Button onClick={handleStartCreate}>
						<Plus className="size-4 mr-2" />
						New Queue
					</Button>
				</DialogFooter>
			</>
		);
	}

	// Render create/edit form
	function renderForm() {
		const isCreate = mode === 'create';

		return (
			<>
				<DialogHeader>
					<DialogTitle>{isCreate ? 'Create Queue' : 'Edit Queue'}</DialogTitle>
					<DialogDescription>
						{isCreate
							? 'Create a new queue to organize your links.'
							: `Editing "${editingQueue?.name}"`}
					</DialogDescription>
				</DialogHeader>

				<div className="py-4 space-y-4">
					{/* Name */}
					<div className="space-y-2">
						<Label htmlFor="queue-name">Name</Label>
						<Input
							id="queue-name"
							value={formData.name}
							onChange={(e) => handleNameChange(e.target.value)}
							placeholder="My Queue"
							autoFocus
						/>
					</div>

					{/* Slug */}
					<div className="space-y-2">
						<Label htmlFor="queue-slug">Slug</Label>
						<div className="flex items-center gap-2">
							<span className="text-muted-foreground">/</span>
							<Input
								id="queue-slug"
								value={formData.slug}
								onChange={(e) =>
									setFormData((prev) => ({ ...prev, slug: slugify(e.target.value) }))
								}
								placeholder="my-queue"
								className="flex-1"
							/>
						</div>
						<p className="text-xs text-muted-foreground">URL-safe identifier for API access</p>
					</div>

					{/* Color */}
					<div className="space-y-2">
						<Label>Color</Label>
						<div className="flex items-center gap-2 flex-wrap">
							{PRESET_COLORS.map((color) => (
								<button
									key={color}
									type="button"
									className={cn(
										'size-8 rounded-full transition-all',
										formData.color === color
											? 'ring-2 ring-offset-2 ring-foreground scale-110'
											: 'hover:scale-110'
									)}
									style={{ backgroundColor: color }}
									onClick={() => setFormData((prev) => ({ ...prev, color }))}
								/>
							))}
							<Input
								type="color"
								value={formData.color}
								onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
								className="w-10 h-8 p-0 border-0 cursor-pointer"
							/>
						</div>
					</div>

					{/* Icon (emoji) */}
					<div className="space-y-2">
						<Label htmlFor="queue-icon">Icon (emoji, optional)</Label>
						<Input
							id="queue-icon"
							value={formData.icon}
							onChange={(e) =>
								setFormData((prev) => ({ ...prev, icon: e.target.value.slice(0, 4) }))
							}
							placeholder="📚"
							className="w-20"
							maxLength={4}
						/>
					</div>

					{/* Preview */}
					<div className="space-y-2">
						<Label>Preview</Label>
						<div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
							<span
								className="size-4 rounded-full shrink-0"
								style={{ backgroundColor: formData.color }}
							/>
							{formData.icon && <span className="text-lg">{formData.icon}</span>}
							<span className="font-medium">{formData.name || 'Queue Name'}</span>
						</div>
					</div>
				</div>

				{error && <p className="text-sm text-destructive mb-4">{error}</p>}

				<DialogFooter>
					<Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
						<X className="size-4 mr-2" />
						Cancel
					</Button>
					<Button onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? 'Saving...' : isCreate ? 'Create Queue' : 'Save Changes'}
					</Button>
				</DialogFooter>
			</>
		);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				{mode === 'list' ? renderList() : renderForm()}
			</DialogContent>
		</Dialog>
	);
}
